/**
 * ターン終了記録スキーマ — 終了理由の区分・記録の形・しきい値・表示状態の導出
 *
 * 管理コンソールの監査で「なぜこのタスクはここで終わったのか」を判別するための記録。
 * 区分の値とスキーマの唯一の出所であり、desktop・infra・web はここから import する。
 */
import { z } from 'zod';
// ========================================
// 定数
// ========================================
/** 記録するエラーメッセージの最大長（超える部分は切り詰めて truncated を立てる） */
export const TURN_ERROR_MESSAGE_MAX_LENGTH = 2000;
/**
 * 実行中のまま更新がないタスクを「記録途絶」とみなすまでの経過時間。
 * 通常のターン実行時間で誤判定しないよう、十分長く取る。
 */
export const STALE_RUNNING_THRESHOLD_MS = 24 * 60 * 60 * 1000;
/** 理由・コードを境界で受けるときの長さ上限 */
const TURN_END_CODE_MAX_LENGTH = 64;
// ========================================
// 既知の値
// ========================================
/** ターンの終了理由（既知の値） */
export const TURN_END_REASONS = [
    /** 正常完了 */
    'completed',
    /** 利用者の中断操作 */
    'user-interrupted',
    'max-steps-reached',
    'session-budget-exceeded',
    'max-continuations-reached',
    'no-progress',
    'gave-up',
    'error',
    /** アプリ終了による中断 */
    'app-shutdown',
    /** 委譲元による取り消し */
    'origin-cancelled',
];
/** 既知のエラーコード */
export const TURN_ERROR_CODES = [
    'COST_LIMIT_EXCEEDED',
    'SYSTEM_COST_LIMIT_EXCEEDED',
    'RATE_LIMIT',
    'AUTH_ERROR',
    'NETWORK_ERROR',
    'PROVIDER_ERROR',
    'EXECUTION_ERROR',
    'UNKNOWN',
];
export function isKnownTurnEndReason(value) {
    return TURN_END_REASONS.includes(value);
}
export function isKnownTurnErrorCode(value) {
    return TURN_ERROR_CODES.includes(value);
}
// ========================================
// スキーマ
// ========================================
/**
 * エラー情報。コードは既知値が TurnErrorCode だが、前方互換のため境界では文字列で受ける。
 */
export const TurnErrorInfoSchema = z.object({
    code: z.string().max(TURN_END_CODE_MAX_LENGTH),
    /** 伏字化済み。内容が取得できなかった場合は省略する */
    message: z.string().max(TURN_ERROR_MESSAGE_MAX_LENGTH).optional(),
    /** message を上限で切り詰めた場合 true */
    truncated: z.boolean().optional(),
});
/**
 * 1 ターンの終了記録。理由は既知値が TurnEndReason だが、境界では文字列で受ける。
 */
export const TurnEndRecordSchema = z.object({
    reason: z.string().max(TURN_END_CODE_MAX_LENGTH),
    /** ISO 8601 */
    endedAt: z.string(),
    /** reason === 'error' のときのみ */
    error: TurnErrorInfoSchema.optional(),
});
/** メッセージパートとしての終了記録 */
export const TurnEndPartSchema = TurnEndRecordSchema.extend({
    type: z.literal('turn-end'),
});
/**
 * タスクの状態と直近の終了記録から、表示用の終了状態を導出する。
 *
 * - 実行中は記録より優先する（新しいターンが始まっている）
 * - 実行中で最終更新から STALE_RUNNING_THRESHOLD_MS 以上経過していれば「記録途絶」
 * - 未知の状態値でも例外を投げず「理由未記録」とする
 */
export function deriveTaskEndState(input, now) {
    if (input.status === 'running') {
        const updatedAtMs = typeof input.updatedAt === 'number' ? input.updatedAt : Date.parse(input.updatedAt);
        // 更新時刻が読めないものは「最近更新された」と確かめられないため途絶側に倒す
        if (!Number.isFinite(updatedAtMs) || now - updatedAtMs >= STALE_RUNNING_THRESHOLD_MS) {
            return { kind: 'stale' };
        }
        return { kind: 'running' };
    }
    if (input.lastTurnEnd) {
        return { kind: 'ended', record: input.lastTurnEnd };
    }
    return { kind: 'unrecorded' };
}
//# sourceMappingURL=task-end-schemas.js.map